import React, { useEffect, useMemo, useState } from "react";
import "../App.css";
import ImageMapper from "react-img-mapper";
import Modal from "../Component/Popup";
import Loader from "../Reusable/Loader/logoLoad";
import { useParams, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";

// Safari / WKWebView will not paint a canvas larger than 16,777,216 px, and on
// low-memory devices getContext('2d') returns null well below that. react-img-mapper
// sizes its canvas to whatever we render the image at, so keep it under a safe margin.
const MAX_CANVAS_PX = 12e6;

const calculateCenter = (coords) => {
    if (!coords || coords.length < 2) return { x: 0, y: 0 };
    const xCoords = coords.filter((_, i) => i % 2 === 0);
    const yCoords = coords.filter((_, i) => i % 2 !== 0);
    const xCenter = (Math.min(...xCoords) + Math.max(...xCoords)) / 2;
    const yCenter = (Math.min(...yCoords) + Math.max(...yCoords)) / 2;
    return { x: xCenter, y: yCenter };
};

const getCenterCoords = (areas) =>
    areas.map((area) => ({ ...area, center: calculateCenter(area.coords) }));

const fixPolyCoords = (coords) => {
    if (!coords || coords.length < 6) return coords;
    const points = [];
    for (let i = 0; i < coords.length; i += 2) {
        if (!isNaN(coords[i]) && !isNaN(coords[i + 1])) {
            points.push({ x: coords[i], y: coords[i + 1] });
        }
    }
    if (points.length < 3) return coords;

    const cx = points.reduce((sum, p) => sum + p.x, 0) / points.length;
    const cy = points.reduce((sum, p) => sum + p.y, 0) / points.length;

    points.sort((a, b) => Math.atan2(a.y - cy, a.x - cx) - Math.atan2(b.y - cy, b.x - cx));

    const result = [];
    points.forEach((p) => {
        result.push(p.x, p.y);
    });
    return result;
};

const isCrossPlotItem = (item) => {
    if (!item) return false;
    return (
        item?.isCrossPlot == 1 ||
        item?.isCrossPlot === "1" ||
        item?.isCrossPlot === true ||
        item?.isCrossPlot === "true" ||
        item?.is_cross_plot == 1 ||
        item?.is_cross_plot === "1" ||
        item?.is_cross_plot === true ||
        item?.is_cross_plot === "true" ||
        item?.isCross == 1 ||
        item?.isCross === "1" ||
        item?.crossPlot == 1 ||
        item?.crossPlot === "1"
    );
};

const ColorCodes = (status) => {
    switch (status) {
        case 1:
            return {
                Dark: "rgba(0, 168, 76, 1)",
                Light: "rgba(24, 139, 24, 0.5)",
                Status: "Available",
            };
        case 2:
            return {
                Dark: "rgba(25, 153, 161, 1)",
                Light: "rgba(25, 153, 161, 0.5)",
                Status: "Registered",
            };
        case 3:
            return {
                Dark: "rgba(239, 72, 54, 1)",
                Light: "rgba(239, 72, 54, 0.5)",
                Status: "Allotted",
            };
        case 4:
            return {
                Dark: "rgba(255, 140, 0, 1)",
                Light: "rgba(255, 215, 0, 0.6)",
                Status: "Reserved",
            };
        case 5:
            return {
                Dark: "rgba(239, 72, 54, 1)",
                Light: "rgba(239, 72, 54, 0.5)",
                Status: "Allotted",
            };
        case 7:
            return {
                Dark: "rgb(193, 154, 107)",
                Light: "rgb(193, 154, 107)",
                Status: "Mortgage",
            };
        default:
            return {
                Dark: "rgba(0, 0, 0, 1)",
                Light: "rgba(255, 255, 255, 0.6)",
                Status: "Unknown",
            };
    }
};

function App() {
    const [loading, setLoading] = useState(true);
    const [searchParams] = useSearchParams();
    const { Id } = useParams();
    const [extendedAreas, setExtendArea] = useState([]);
    const [PlotsCount, setPlotsCount] = useState({
        Available: 0,
        Alloted: 0,
        mortagage: 0,
        reserved: 0,
        registeredPlots: 0,
        total: 0,
    });
    const [layoutImage, setLayoutImage] = useState("");
    const [metagDetails, setMeta] = useState({ name: "", desc: "" });
    const [ModalData, setModalData] = useState({});
    const [showModal, setShowModal] = useState(false);
    const [IsIos, setIsIos] = useState(false);
    const [modalAnchor, setModalAnchor] = useState({ x: 0, y: 0 });
    const [data, setdata] = useState([]);
    const [fetchError, setFetchError] = useState("");
    // Natural pixel size of the layout image, probed before we hand it to ImageMapper.
    const [imgNatural, setImgNatural] = useState({ width: 0, height: 0 });
    const [imgError, setImgError] = useState("");

    useEffect(() => {
        const userAgent = navigator.userAgent;
        if (/iPhone|iPad|iPod/i.test(userAgent)) {
            setIsIos(true);
        }
    }, []);

    useEffect(() => {
        if (!Id) {
            setLoading(false);
            setFetchError("No venture id in the URL.");
            return;
        }

        const controller = new AbortController();
        let cancelled = false;

        (async () => {
            setLoading(true);
            setFetchError("");
            try {
                const response = await fetch(
                    `https://api.adurigroup.com/api/v1/associate/getVenture/${encodeURIComponent(Id)}`,
                    { signal: controller.signal }
                );

                if (!response.ok) {
                    throw new Error(`Server returned ${response.status}`);
                }

                const res = await response.json();
                if (cancelled) return;

                if (res.Status != 1) {
                    throw new Error(res?.Message || "Venture data unavailable.");
                }

                const plots = Array.isArray(res.plots) ? res.plots : [];
                setdata(plots);
                setPlotsCount({
                    Alloted: res?.PlotsCount?.allotedPlots,
                    Available: res?.PlotsCount?.availablePlots,
                    registeredPlots: res?.PlotsCount?.registeredPlots,
                    mortagage: res?.PlotsCount?.mortgagePlots,
                    reserved: res?.PlotsCount?.reservedPlots,
                    total: res?.PlotsCount?.totalPlots,
                });
                setMeta({ name: res?.ventureName, desc: res?.ventureOverview });
                setExtendArea(
                    getCenterCoords(
                        plots.map((item) => ({
                            plot_number: item.plot_number,
                            coords: item.plot_coordinates
                                ? item.plot_coordinates.split(",").map(Number)
                                : [],
                            isCrossPlot: isCrossPlotItem(item),
                        }))
                    )
                );
                setLayoutImage(res?.layoutImage || "");
            } catch (error) {
                if (cancelled || error.name === "AbortError") return;
                console.error("Error fetching data:", error.message);
                setFetchError(error.message || "Could not load this venture.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [Id]);

    // Probe the layout image up front: we need its natural size to pick a canvas
    // size, and this is also the only place a broken/missing image URL surfaces.
    useEffect(() => {
        if (!layoutImage) return;
        let cancelled = false;
        setImgError("");
        setImgNatural({ width: 0, height: 0 });

        const probe = new window.Image();
        probe.onload = () => {
            if (cancelled) return;
            if (!probe.naturalWidth || !probe.naturalHeight) {
                setImgError("Layout image could not be decoded on this device.");
                return;
            }
            setImgNatural({ width: probe.naturalWidth, height: probe.naturalHeight });
        };
        probe.onerror = () => {
            if (!cancelled) setImgError("Layout image failed to load.");
        };
        probe.src = layoutImage;

        return () => {
            cancelled = true;
        };
    }, [layoutImage]);

    const isWeb = searchParams.get("type") === "web";

    // Render the map at a real element size instead of transform-scaling a
    // full-resolution one: that keeps the canvas small enough for WKWebView and
    // makes the scroll box match what is actually drawn. Rendering (and then
    // pinch-zooming) a near-full-resolution canvas crashes/blanks the WKWebView
    // on-device, so don't allocate anywhere close to the 16.7M px ceiling.
    const { scaleFactor, displayW, displayH } = useMemo(() => {
        if (!imgNatural.width || !imgNatural.height) {
            return { scaleFactor: 0, displayW: 0, displayH: 0 };
        }
        const base = isWeb ? 0.5 : 0.3;
        const scaledPx = imgNatural.width * imgNatural.height * base * base;
        const clamp = scaledPx > MAX_CANVAS_PX ? Math.sqrt(MAX_CANVAS_PX / scaledPx) : 1;
        const scale = base * clamp;
        return {
            scaleFactor: scale,
            displayW: Math.max(1, Math.round(imgNatural.width * scale)),
            displayH: Math.max(1, Math.round(imgNatural.height * scale)),
        };
    }, [imgNatural.width, imgNatural.height, isWeb]);

    const MAP = useMemo(
        () => ({
            name: "my-map",
            areas: data.map((item, index) => {
                const isCross = isCrossPlotItem(item);
                const plotBgColor = isCross
                    ? "rgba(255, 255, 255, 1)"
                    : item?.isMortgage == 1
                        ? ColorCodes(7).Dark
                        : ColorCodes(item.plot_status).Dark;

                return {
                    id: index,
                    title: "Hardwood",
                    shape: "poly",
                    name: "1",
                    preFillColor: plotBgColor,
                    strokeColor: "black",
                    fillColor: plotBgColor,
                    coords: item.plot_coordinates
                        ? fixPolyCoords(item.plot_coordinates.split(",").map(Number))
                        : [],
                    plot_status: item.plot_status,
                    sector_type: item.sector_type,
                    isMortgage: item?.isMortgage,
                    isCrossPlot: isCross ? 1 : 0,
                    facing_type: item.facing_type,
                    plot_number: item.plot_number,
                    dimensions: item.dimensions,
                    plot_area_sqy: item?.plot_area_sqy,
                    crossPlotSqy: item?.crossPlotSqy,
                };
            }),
        }),
        [data]
    );

    const handleMouseEnter = (area) => {
        setModalData(area);
        const [x = 0, y = 0] = area.coords || [];
        setModalAnchor({ x, y });
        setShowModal(true);
    };

    const handleMouseLeave = (e) => { };

    if (loading) return <Loader />;

    return (
        <div className="wrapper_img">
            <Helmet>
                <meta charSet="utf-8" />
                <title>{metagDetails.name}</title>
                <meta property="og:description" content={metagDetails.desc || metagDetails.name} />
            </Helmet>

            {isWeb && (
                <div className="Count_table">
                    <h3>Total Plots: {PlotsCount.total}</h3>
                    <div className="table_data_wrapper">
                        <div className="table_data" style={{ background: "rgba(0, 168, 76, 1)" }}>
                            <h3>Available</h3>
                            <p>{PlotsCount.Available}</p>
                        </div>
                        <div className="table_data" style={{ background: "rgba(239, 72, 54, 1)" }}>
                            <h3>Alloted</h3>
                            <p>{PlotsCount.Alloted}</p>
                        </div>
                        <div className="table_data" style={{ background: "rgba(25, 153, 161, 1)" }}>
                            <h3>Registered</h3>
                            <p>{PlotsCount.registeredPlots || 0}</p>
                        </div>
                        <div className="table_data" style={{ background: "rgb(193, 154, 107)" }}>
                            <h3>Mortgage</h3>
                            <p>{PlotsCount.mortagage}</p>
                        </div>
                        <div className="table_data" style={{ background: "rgba(255, 140, 0, 1)" }}>
                            <h3>Reserved</h3>
                            <p>{PlotsCount.reserved}</p>
                        </div>
                    </div>
                </div>
            )}

            {fetchError || imgError || !layoutImage ? (
                <div className="map_error">
                    {fetchError || imgError || "This venture has no layout image."}
                </div>
            ) : !displayW ? (
                <Loader />
            ) : (
                <div
                    className="image-outer-wrapper"
                    style={{
                        width: "100%",
                        height: `${displayH}px`,
                        overflow: "auto",
                        WebkitOverflowScrolling: "touch",
                        position: "relative",
                    }}>
                    <div
                        className="image-container"
                        style={{
                            position: "relative",
                            width: `${displayW}px`,
                            height: `${displayH}px`,
                        }}>
                        <ImageMapper
                            src={layoutImage}
                            map={MAP}
                            width={displayW}
                            height={displayH}
                            imgWidth={imgNatural.width}
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                        />

                        {extendedAreas.map((area, index) => {
                            const isCross = isCrossPlotItem(area);
                            return (
                                <span
                                    key={area.plot_number || index}
                                    className="tooltip"
                                    style={{
                                        position: "absolute",
                                        top: `${area.center.y * scaleFactor}px`,
                                        left: `${area.center.x * scaleFactor}px`,
                                        transform: `translate(-50%, -50%) scale(${scaleFactor})`,
                                        transformOrigin: "center center",
                                        fontSize: IsIos ? "8.7px" : "18px",
                                        fontWeight: isCross ? "bold" : "normal",
                                        zIndex: 1000,
                                        pointerEvents: "none",
                                        userSelect: "none",
                                    }}>
                                    {area.plot_number}
                                </span>
                            );
                        })}

                        {showModal && (
                            <div
                                style={{
                                    position: "absolute",
                                    left: `${modalAnchor.x * scaleFactor}px`,
                                    top: `${modalAnchor.y * scaleFactor}px`,
                                    transform: `scale(${scaleFactor})`,
                                    transformOrigin: "top left",
                                    zIndex: 9999,
                                }}>
                                <Modal
                                    ModalData={ModalData}
                                    x={-85}
                                    y={-140}
                                    onClose={() => setShowModal(false)}
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default App;
